import { useEffect, useMemo, useState } from 'react';
import {
  foldPayers,
  type FoldResult,
  mergeStatusAndEvidence,
  usePayerOrganizationStatusQuery,
} from '@features/payer-connections';
import { Stack, Typography } from '@mui/material';

import { IntakePill } from './shell/IntakePill';
import { metaSx } from './shell/tokens';

type Tone = 'green' | 'slate';

interface GoLiveState {
  label: string;
  tone: Tone;
  detail?: string;
}

/**
 * Payers the Payer Connections fold holds at `ready` (every current rail LIVE)
 * count as ready to bill. Canceled payers are left out of the total.
 */
function goLiveCounts(fold: FoldResult) {
  // /status carries no claim evidence, so the fold never reaches `live`.
  const ready = fold.counts.ready;
  const total = fold.counts.needs_us + fold.counts.needs_customer + fold.counts.waiting + ready;
  return { ready, total, live: ready > 0 };
}

function foldState(fold: FoldResult): GoLiveState {
  const { ready, total, live } = goLiveCounts(fold);
  if (!total) return { label: 'No enrollments', tone: 'slate', detail: 'No payer enrollments yet' };
  const detail = `${ready} of ${total} ${total === 1 ? 'payer' : 'payers'} ready to bill`;
  return live
    ? { label: 'Ready to bill', tone: 'green', detail }
    : { label: 'Not ready', tone: 'slate', detail };
}

/**
 * One organization's go-live state, read from Stedi's enrollment status
 * (no claim-receipt lookup) independently of the fleet table.
 */
export function GoLiveStatus({
  organizationId,
  enabled = true,
  onSettled,
}: {
  organizationId: string;
  /** False until the row enters the fleet's load window; it shows "Checking Stedi…" meanwhile. */
  enabled?: boolean;
  /** Called once no read is in flight and there is an answer, so the next row may start. */
  onSettled?: (organizationId: string) => void;
}) {
  const { data, error, isFetching } = usePayerOrganizationStatusQuery(
    { organizationId },
    { skip: !enabled },
  );
  // The query subscribes (and starts any stale refetch) in the commit that
  // enables it, so isFetching is only trustworthy from the next render on.
  // Without this, cached data would settle the row before its refetch starts.
  const [armed, setArmed] = useState(false);
  useEffect(() => setArmed(enabled), [enabled]);
  const fold = useMemo(
    () => (data ? foldPayers(mergeStatusAndEvidence(data.enrollments ?? [], [])) : null),
    [data],
  );
  // Usable data wins over a later refetch error, as in the admin Payer Connections view.
  const usable = data && !data.error && fold ? { data, fold } : null;
  const state: GoLiveState = usable
    ? usable.data.connected
      ? foldState(usable.fold)
      : { label: 'Not connected', tone: 'slate', detail: 'No Stedi account connected' }
    : error || data?.error
      ? { label: 'Unknown', tone: 'slate', detail: 'Status unavailable' }
      : { label: 'Checking Stedi…', tone: 'slate' };
  const settled = armed && !isFetching && Boolean(usable || error || data?.error);
  useEffect(() => {
    if (settled) onSettled?.(organizationId);
  }, [onSettled, organizationId, settled]);
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <IntakePill tone={state.tone} label={state.label} />
      {state.detail && <Typography sx={metaSx}>{state.detail}</Typography>}
    </Stack>
  );
}
